import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import type { Role, User } from "../../api/types";
import { useDialogs } from "../../components/Dialogs";
import { Pill } from "../../components/Pill";
import { ErrorBox, Spinner } from "../../components/Spinner";
import { useCurrentUser } from "../../lib/auth";

export function UserSettings() {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const { prompt, confirm, toast } = useDialogs();
  const { data, isLoading, error } = useQuery({ queryKey: qk.users, queryFn: api.users });
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.users });
  const patch = useMutation({
    mutationFn: (v: { id: string; p: Parameters<typeof api.patchUser>[1] }) => api.patchUser(v.id, v.p),
    onSettled: invalidate,
  });

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("member");
  const create = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.createUser({ username, display_name: displayName, password, role }),
    onSuccess: () => {
      setUsername("");
      setDisplayName("");
      setPassword("");
      setRole("member");
      invalidate();
    },
  });

  const resetPassword = async (u: User) => {
    const pw = await prompt({
      title: `Reset password for ${u.username}`,
      label: "New password",
      inputType: "password",
      confirmLabel: "Reset password",
    });
    if (pw !== null) patch.mutate({ id: u.id, p: { password: pw } }, { onSuccess: () => toast("Password reset", "info") });
  };

  const rename = async (u: User) => {
    const name = await prompt({ title: "Display name", initial: u.display_name, confirmLabel: "Save" });
    if (name !== null) patch.mutate({ id: u.id, p: { display_name: name } });
  };

  return (
    <div>
      {isLoading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id} className={u.active ? "" : "row-inactive"}>
                <td>
                  <button className="link-button" onClick={() => rename(u)} title="Rename">
                    {u.display_name || u.username}
                  </button>
                  <div className="muted small">{u.username}</div>
                </td>
                <td>
                  <select
                    className="input input-inline"
                    value={u.role}
                    disabled={u.id === me.id}
                    onChange={(e) => patch.mutate({ id: u.id, p: { role: e.target.value as Role } })}
                    aria-label={`Role for ${u.username}`}
                  >
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td>{u.active ? <Pill color="green">active</Pill> : <Pill color="gray">deactivated</Pill>}</td>
                <td className="table-actions">
                  <button className="btn btn-sm" onClick={() => resetPassword(u)}>
                    Reset password
                  </button>
                  {u.id !== me.id && (
                    <button
                      className={u.active ? "btn btn-sm btn-ghost-danger" : "btn btn-sm"}
                      onClick={async () => {
                        if (
                          !u.active ||
                          (await confirm({
                            title: `Deactivate ${u.username}?`,
                            body: <p>They will no longer be able to sign in.</p>,
                            danger: true,
                            confirmLabel: "Deactivate",
                          }))
                        )
                          patch.mutate({ id: u.id, p: { active: !u.active } });
                      }}
                    >
                      {u.active ? "Deactivate" : "Reactivate"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form
        className="edit-card form-stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <h3 className="section-label">Create user</h3>
        <div className="field-row">
          <label className="field">
            <span className="field-label">Username</span>
            <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
          </label>
          <label className="field">
            <span className="field-label">Display name</span>
            <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">Initial password</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Role</span>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="member">member</option>
              <option value="admin">admin</option>
            </select>
          </label>
        </div>
        {create.error && <div className="error-box">{create.error.message}</div>}
        <div className="row-actions row-actions-start">
          <button className="btn btn-primary" disabled={create.isPending}>
            Create user
          </button>
        </div>
      </form>
    </div>
  );
}
